import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../layout';
import { api } from '../services/api';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const BASE_URL = API_URL.replace('/api', '');

const DocumentsSection = ({ ownerType, ownerId, ownerName, readOnly = false }) => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        file: null
    });

    useEffect(() => {
        if (ownerId) {
            fetchDocuments();
        }
    }, [ownerType, ownerId]);

    const fetchDocuments = async () => {
        if (!ownerId) return;

        setIsLoading(true);
        try {
            const response = await api.getPrivateDocuments(ownerType, ownerId);
            if (response.status === 'success') {
                setDocuments(response.documents || []);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
            const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif'];
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Tipo file non valido',
                    text: 'Sono ammessi solo file PDF, JPG, PNG e GIF'
                });
                e.target.value = '';
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File troppo grande',
                    text: 'La dimensione massima consentita è 10MB'
                });
                e.target.value = '';
                return;
            }

            setFormData(prev => ({
                ...prev,
                file: file,
                title: prev.title || file.name.replace(/\.[^/.]+$/, '')
            }));
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!formData.file) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Seleziona un file'
            });
            return;
        }

        if (!formData.title) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Inserisci un titolo'
            });
            return;
        }

        setIsUploading(true);
        try {
            const response = await api.uploadPrivateDocument(
                formData.file,
                ownerType,
                ownerId,
                formData.title,
                formData.description
            );

            if (response.status === 'success') {
                fetchDocuments();
                setShowUploadForm(false);
                setFormData({ title: '', description: '', file: null });
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                Swal.fire({
                    icon: 'success',
                    title: 'Caricato',
                    text: 'Documento caricato con successo',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: error.message || 'Impossibile caricare il documento'
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (document) => {
        const result = await Swal.fire({
            title: 'Sei sicuro?',
            text: `Vuoi eliminare il documento "${document.title}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Si, elimina',
            cancelButtonText: 'Annulla'
        });

        if (result.isConfirmed) {
            try {
                await api.deletePrivateDocument(document.id);
                setDocuments(prev => prev.filter(d => d.id !== document.id));
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminato',
                    text: 'Documento eliminato con successo',
                    timer: 1500,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error('Error deleting document:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: 'Impossibile eliminare il documento'
                });
            }
        }
    };

    const handlePreview = (document) => {
        const fileUrl = `${BASE_URL}${document.file_path}`;
        const isPdf = document.mime_type === 'application/pdf' || document.original_filename?.endsWith('.pdf');
        const isImage = document.mime_type?.startsWith('image/');

        if (isPdf || isImage) {
            Swal.fire({
                title: document.title,
                html: isPdf
                    ? `<iframe src="${fileUrl}" style="width:100%; height:60vh; border:none;"></iframe>`
                    : `<img src="${fileUrl}" style="max-width:100%; max-height:60vh;" alt="${document.title}" />`,
                width: '80%',
                showCloseButton: true,
                showConfirmButton: false,
            });
        } else {
            window.open(fileUrl, '_blank');
        }
    };

    const handleDownload = (document) => {
        const fileUrl = `${BASE_URL}${document.file_path}`;
        window.open(fileUrl, '_blank');
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getFileIcon = (mimeType, filename) => {
        const isPdf = mimeType === 'application/pdf' || filename?.endsWith('.pdf');
        const isImage = mimeType?.startsWith('image/');

        if (isPdf) {
            return (
                <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                    <span className="text-red-600 text-xs font-bold">PDF</span>
                </div>
            );
        }

        if (isImage) {
            return (
                <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                    <span className="text-blue-600 text-xs font-bold">IMG</span>
                </div>
            );
        }

        return (
            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                <span className="text-gray-600 text-xs font-bold">FILE</span>
            </div>
        );
    };

    if (!ownerId) {
        return (
            <div className="text-center py-8 text-gray-500">
                Salva prima i dati per poter gestire i documenti riservati.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header with add button */}
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                    Documenti Riservati
                    {ownerName && <span className="text-gray-500 font-normal"> - {ownerName}</span>}
                </h3>
                {!readOnly && (
                    <Button
                        onClick={() => setShowUploadForm(!showUploadForm)}
                        color="primary"
                        size="small"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {showUploadForm ? 'Annulla' : 'Nuovo Documento'}
                    </Button>
                )}
            </div>

            <p className="text-sm text-gray-500">
                Questi documenti saranno visibili solo a questo {ownerType === 'client' ? 'cliente' : 'agente'} nella sezione "Consultazione Documenti".
            </p>

            {/* Upload Form */}
            {showUploadForm && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <form onSubmit={handleUpload} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Seleziona File *
                            </label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png,.gif"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">PDF, JPG, PNG, GIF - Max 10MB</p>
                            {formData.file && (
                                <p className="mt-1 text-sm text-green-600">
                                    File selezionato: {formData.file.name}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Titolo *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                placeholder="Titolo del documento"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Descrizione
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                placeholder="Descrizione opzionale"
                            />
                        </div>

                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Caricamento...
                                    </>
                                ) : (
                                    'Carica Documento'
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            )}

            {/* Documents List */}
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                </div>
            ) : documents.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">Nessun documento riservato</p>
                    {!readOnly && <p className="text-xs text-gray-400">Clicca "Nuovo Documento" per aggiungerne uno</p>}
                </div>
            ) : (
                <div className="space-y-2">
                    {documents.map((doc) => (
                        <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                {getFileIcon(doc.mime_type, doc.original_filename)}
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                                        <span>{doc.original_filename}</span>
                                        <span>-</span>
                                        <span>{formatFileSize(doc.file_size)}</span>
                                        <span>-</span>
                                        <span>{formatDate(doc.created_at)}</span>
                                    </div>
                                    {doc.description && (
                                        <p className="text-xs text-gray-400 mt-1">{doc.description}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => handlePreview(doc)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Visualizza"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => handleDownload(doc)}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded"
                                    title="Scarica"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                </button>
                                {!readOnly && (
                                <button
                                    onClick={() => handleDelete(doc)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    title="Elimina"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DocumentsSection;
